//This will put a button to show saved reports and a button to save the current report on a page, as well as having two corresponding dialogs for these buttons.
//To use this, put this in your HTML:
//  <saved-reports #savedReports></saved-reports>
//Then put this in your TS file:
//  @ViewChild('savedReports', { static: true }) savedReports: SavedReportsComponent;
//Then initialize it by calling the init() function. 
import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { WebService, ApiResponse, ReportFavorite, GetReportFavoritesResponse } from '../web-service/web.service';
import { GridOptions } from 'ag-grid-community';
import { GridFooterComponent } from '../grid-footer/grid-footer.component';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { Formatter } from '../utilities/utilities';

@Component({
  selector: 'saved-reports',
  templateUrl: './saved-reports.component.html',
  styleUrls: ['./saved-reports.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SavedReportsComponent implements OnInit {
  @ViewChild('modalFavorites') modalFavorites;
  @ViewChild('modalAddEdit') modalAddEdit;

  gridFilter: ReportFavorite = new ReportFavorite(); //init with a placeholder so we don't get errors binding in the HTML
  reportTypeNo: number;
  reportTypeNoSaveAs: number;
  rowDataGrid: ReportFavorite[] = [];
  allSavedReportFavs: ReportFavorite[] = [];
  gridOptions: GridOptions;
  oldFavoriteName: string = "";
  msgBox: MessageBoxComponent;
  callbackValidDates: Function;
  callbackBeforeSave: Function;
  callbackViewReport: Function;
  filterSqlHash: any; //hash table of all the filterValues and their translated filterSqls. The filterValue gets translated to SQL on the server and passed back to us, and we keep a copy of it here and pass it back to the server so that it saves the server time (it doesn't have to translate it each time).
  static staticInstance: SavedReportsComponent;

  constructor(private webService: WebService) {
    this.gridOptions = <GridOptions>{
      pagination: false,
      suppressCellSelection: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowClickSelection: true,
      suppressColumnVirtualisation: true, //This makes it resize the columns correctly at page load (even the ones not shown yet because of scrolling)
      defaultColDef: { sortable: true, filter: true, resizable: true },
      columnDefs: [
        { headerName: 'Actions', field: 'reportFavoriteNo', suppressMenu: true, sortable: false, cellRenderer: this.actionCellRendererFunc, width: 85 },
        { headerName: 'Report name', field: 'reportName', width: 225 },
        { headerName: 'Last saved', field: 'lastSaved', width: 90, comparator: Formatter.stringDateComparator, cellStyle: { textAlign: "right" } },
      ],
      onFirstDataRendered: (params) => {
        GridFooterComponent.sizeColumnWidths(null, "gridSavedReports", this.gridOptions, "Giving_Report_Favorites", true);
      }
    };
  }

  ngOnInit() {
    SavedReportsComponent.staticInstance = this;
  }

  //Call this to initalize the control. This control is designed to go along with a ReportFavorite on a report page with filtering options. callbackValidDates will be called when saving a fav to validate that the dates are OK. callbackBeforeSave will be called when the user is saving a fav. callbackViewReport will be called when the user selects a fav to view.
  initSavedReports(gridFilter: ReportFavorite, msgBox: MessageBoxComponent, reportTypeNo: number, callbackValidDates: Function, callbackBeforeSave: Function, callbackViewReport: Function, filterSqlHash: any = null) {
    this.gridFilter = gridFilter;
    this.msgBox = msgBox;
    this.reportTypeNo = reportTypeNo;
    this.callbackValidDates = callbackValidDates;
    this.callbackBeforeSave = callbackBeforeSave;
    this.callbackViewReport = callbackViewReport;
    this.filterSqlHash = filterSqlHash;
  }

  //This is used (after we get a response from the server of a new list of report favorites) to update the list on the dialog.
  updateGridFavs(apiResponse: GetReportFavoritesResponse) {
    if (ApiResponse.checkStatus(apiResponse)) {
      //The list returned from the server has the saved reports for all report types. We save this list but make another list that only has the saved reports for this report type to show in the grid.
      this.allSavedReportFavs = apiResponse.reportFavoriteList;
      this.rowDataGrid = this.allSavedReportFavs.filter(x => x.reportTypeNo == this.reportTypeNo);
      this.gridOptions.api.setRowData(this.rowDataGrid);
    }
  }

  //This funtion returns either an HTML string or object that is the HTML for the cell.
  //passed in: params.data is the object that is the data source for the row.
  actionCellRendererFunc(params) {
    var span = document.createElement('span');

    var link = document.createElement('a');
    link.innerHTML = 'View';
    link.setAttribute('href', 'javascript:void(0);');
    link.addEventListener('click', () => { //This is the event handler for when the user clicks the View link for one of the favs in the grid.
      if (SavedReportsComponent.staticInstance.callbackViewReport) //only call the callback function if it exists.
        SavedReportsComponent.staticInstance.callbackViewReport(params.data);
      SavedReportsComponent.staticInstance.modalFavorites.hide();
      SavedReportsComponent.staticInstance.oldFavoriteName = params.data.reportName;
    });
    span.appendChild(link);

    var link = document.createElement('a');
    link.innerHTML = 'Delete';
    link.setAttribute('href', 'javascript:void(0);');
    link.className = "link-in-grid-row";
    link.addEventListener('click', () => { //This is the event handler for when the user clicks the Delete link for one of the favs in the grid.
      SavedReportsComponent.staticInstance.deleteFavorite(params.data);
    });
    span.appendChild(link);

    return span;
  }

  // Open the dialog to save a favorite
  addEditFavorite() {
    if (this.callbackValidDates && !this.callbackValidDates()) //only call the callback function if it exists.
      return false;
    this.modalAddEdit.show();
    this.gridFilter.reportName = this.oldFavoriteName;
    this.reportTypeNoSaveAs = this.reportTypeNo;
    //We have to use setTimeout to set the focus to the first field because you can't set focus until it appears, which takes a moment.
    setTimeout(() => { $("#ReportName").focus() }, 300);
  }

  // Get ready to save reportFavorite to the DB. We do validation first.
  saveAddEditReportFavorite() {
    if ($("#ReportName").val() == '') {
      $("#validationErrorTd").text("Fields marked with * cannot be blank");
      $("#validationMsg").css("display", "");
      $("#ReportName").addClass("validation-error");
      return;
    }

    //There's no validation error
    $("#validationMsg").css("display", "none");

    //See if there's an existing saved report with the same name and reportNo that we are overwriting. If there is, we show a confirmation dialog.
    let matchingFav: ReportFavorite = this.allSavedReportFavs.find(x => x.reportName.toLowerCase() == this.gridFilter.reportName.toLowerCase() && x.reportTypeNo == this.reportTypeNoSaveAs);
    if (matchingFav) {
      this.msgBox.show("Confirm", `This will overwrite the existing favorite report with this name. Are you sure you want to continue?`, true,
        () => {
          this.finishAddEditReportFavorite(matchingFav.reportFavoriteNo);
        });
    }
    else
      this.finishAddEditReportFavorite(0);
  }

  // Save reportFavorite to the DB and close the dialog.
  finishAddEditReportFavorite(favNo: number = 0) {
    //gridFilter represents the fav being saved. We fill in a few of its fields before saving it.
    this.gridFilter.reportFavoriteNo = favNo;
    this.gridFilter.reportTypeNo = this.reportTypeNoSaveAs;
    //If the advanced filter value has previously been translated into SQL and saved in filterSqlHash, then we put that SQL into the filterSql field. However, The filterSql from the server (that we store in filterSqlHash) has two values stuck together separated by a \b character. The first is the filterSql (which is the what the advanced filter filterValue translated into SQL is), and the second is a delimited list of donorNo's included in the advanced filter query. We only want the former to be in the filterSql field, so we split it out and ignore the latter.
    if (!this.filterSqlHash || !this.filterSqlHash[this.gridFilter.filterValue])
      this.gridFilter.filterSql = null;
    else
      this.gridFilter.filterSql = this.filterSqlHash[this.gridFilter.filterValue].split('\b')[0];

    if (this.callbackBeforeSave) //only call the callback function if it exists.
      this.callbackBeforeSave();

    //We save to the DB. The API method will return the new list of reportFavorites to update the grid.
    this.gridFilter.donorNosForAdvancedQuery = ""; //This information isn't saved for each report, so we make sure it's blanked out.
    this.webService.reports_AddEditReportFavorite(this.gridFilter).subscribe(
      apiResponse => {
        this.updateGridFavs(apiResponse);
        if (apiResponse.errorCode == 0) { //only close dialog if save was successful
          this.modalAddEdit.hide();
          this.oldFavoriteName = this.gridFilter.reportName;
        }
      }
    );
  }

  //Cancel out of the add/edit report favorite dailog
  cancelAddEditReportFavorite() {
    this.modalAddEdit.hide();
    // Get rid of validation errors for when the dialog appears next time.
    $("#ReportName").removeClass("validation-error");
    $("#validationMsg").css("display", "none");
  }

  //Event handler for when the show favorites button is clicked.
  showFavorites() {
    this.modalFavorites.show();
  }

  //Delete pledge. The API method will return the new list of pledges to update the grid.
  deleteFavorite(fav: ReportFavorite) {
    this.msgBox.show("Confirm", `Are you sure you want to delete this favorite report (${fav.reportName})? This action cannot be undone.`, true,
      () => {
        this.webService.reports_DeleteReportFavorite(fav.reportFavoriteNo).subscribe(
          apiResponse => this.updateGridFavs(apiResponse)
        );
      });
  }
}
